// "Waiting for your approval" — the items an Admin / Super Admin has to decide,
// on their dashboard instead of only behind a menu (client, 2026-10-24):
// invoice edits asked for after an invoice is final, and the material requests
// Dispatch raises in Inventory. Each row opens the full review screen — an
// invoice edit is approved there, after seeing the before/after lines.
import { useNavigate } from 'react-router-dom';
import { CheckSquare, ChevronRight, PackageOpen } from 'lucide-react';
import { useInvoiceEditRequests } from '@/features/invoices/hooks/useInvoiceEditRequests';
import { useMaterialRequests } from '@/features/inventory/hooks/useMaterials';
import { formatINR } from '@/shared/utils/currency';
import { ROUTES } from '@/routes/paths';

const DOC_LABEL: Record<string, string> = { direct_sale: 'Invoice', tax_invoice: 'Tax invoice', sales_order: 'Sales order' };

function ago(iso?: string | null): string {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface Row {
  key: string;
  kind: 'invoice' | 'material';
  at: string;
  title: string;
  detail: string;
  who: string;
  urgent?: boolean;
  to: string;
}

export function ApprovalsQueueCard({ inventory = true }: { inventory?: boolean }) {
  const navigate = useNavigate();
  const edits = useInvoiceEditRequests('pending');
  const materials = useMaterialRequests('pending_approval', '', false, inventory);

  const rows: Row[] = [
    ...(edits.data ?? []).map((e): Row => {
      const delta = (e.proposedGrandTotal || 0) - (e.previousGrandTotal || 0);
      const amount = e.computedResult?.dispatchHandoff
        ? 'dispatch details'
        : `${formatINR(e.previousGrandTotal)} → ${formatINR(e.proposedGrandTotal)}${delta ? ` (${delta > 0 ? '+' : '−'}${formatINR(Math.abs(delta))})` : ''}`;
      return {
        key: `e-${e._id}`, kind: 'invoice', at: e.requestedAt,
        title: `${DOC_LABEL[e.documentType] || 'Invoice'} ${e.documentNumber || ''} — edit`,
        detail: [e.customerName, amount, e.reason || e.bdmReason].filter(Boolean).join(' · '),
        who: e.requestedByName, to: ROUTES.invoiceEditApprovals,
      };
    }),
    ...(inventory ? (materials.data?.requests ?? []).filter((m) => m.status === 'pending_approval') : []).map((m): Row => ({
      key: `m-${m._id}`, kind: 'material', at: m.createdAt,
      title: `Material request ${m.requestNumber}`,
      detail: [
        `${m.lines.length} item${m.lines.length === 1 ? '' : 's'}: ${m.lines.slice(0, 2).map((l) => `${l.itemName} ${l.quantity} ${l.unit}`).join(', ')}${m.lines.length > 2 ? '…' : ''}`,
        m.reference,
        m.requiredBy ? `needed by ${new Date(m.requiredBy).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : '',
      ].filter(Boolean).join(' · '),
      who: m.requestedByName, urgent: m.priority === 'urgent' || m.priority === 'high',
      to: `${ROUTES.inventory}?tab=requests&request=${m._id}`,
    })),
  ].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

  const loading = edits.isLoading || (inventory && materials.isLoading && !edits.data?.length);
  const failed = edits.isError && (!inventory || materials.isError);
  const shown = rows.slice(0, 8);

  return (
    <section className="flex flex-col overflow-hidden rounded-card border border-border/80 bg-card shadow-surface-1">
      <div className="flex items-center justify-between gap-2 border-b border-border bg-surface-container-low/60 px-5 py-3">
        <h3 className="flex items-center gap-2 text-body-md font-semibold text-on-surface">
          Waiting for your approval
          {rows.length > 0 && <span className="rounded-full bg-danger/10 px-2 py-0.5 text-data-xs font-bold tabular-nums text-danger-text">{rows.length}</span>}
        </h3>
        <span className="text-data-xs font-semibold text-on-surface-variant">oldest first</span>
      </div>
      <div className="p-3 sm:p-4">
        {loading ? (
          <div className="py-5 text-center text-body-sm text-on-surface-subtle">Loading…</div>
        ) : failed ? (
          <div className="py-5 text-center text-body-sm text-warning-text">Could not load approvals — try Refresh.</div>
        ) : rows.length === 0 ? (
          <div className="py-4 text-center text-body-md font-semibold text-success-text">Nothing is waiting for your approval.</div>
        ) : (
          <ul className="flex flex-col gap-2">
            {shown.map((r) => {
              const Icon = r.kind === 'invoice' ? CheckSquare : PackageOpen;
              return (
                <li key={r.key}>
                  <button type="button" onClick={() => navigate(r.to)}
                    className="flex w-full items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2.5 text-left transition hover:border-primary/40 hover:shadow-xs">
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${r.kind === 'invoice' ? 'bg-danger/10 text-danger-text' : 'bg-info/10 text-info-text'}`}>
                      <Icon className="h-4 w-4" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className="truncate text-data-md font-semibold text-on-surface">{r.title}</span>
                        {r.urgent && <span className="rounded bg-warning/15 px-1.5 text-data-xs font-bold uppercase text-warning-text">Urgent</span>}
                      </span>
                      <span className="block truncate text-body-sm text-on-surface-variant" title={r.detail}>{r.detail}</span>
                      <span className="block text-data-xs text-on-surface-subtle">{r.who || 'Someone'} · {ago(r.at)}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-0.5 text-body-sm font-semibold text-primary">Review<ChevronRight className="h-3.5 w-3.5" /></span>
                  </button>
                </li>
              );
            })}
            {rows.length > shown.length && (
              <li className="pt-1 text-center text-body-sm text-on-surface-variant">+{rows.length - shown.length} more — open Invoice edit approvals or Inventory → Requests</li>
            )}
          </ul>
        )}
      </div>
    </section>
  );
}
